import { Component, OnDestroy, OnInit } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { CurrencyPipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { Subject, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GLOBAL_SEARCH_MIN_LENGTH, GlobalSearchService } from '../../../services/global-search.service';
import { GlobalSearchItem, GlobalSearchModulePage } from '../interfaces/global-search.interface';
import { RemindersService } from '../reminders/services/reminders.service';
import { AddReminderModalComponent } from '../reminders/components/add-reminder-modal/add-reminder-modal.component';
import { ToasterService } from '../../../services/core/toaster.service';

const PER_PAGE = 20;

/**
 * "View all" page for Global Search: /search/:module?q={term}&page={n}
 * Shows one module's full, paginated results from GET /api/v1/search/{module}.
 * Authorization is enforced by the backend; this page only renders what it returns.
 */
@Component({
  selector: 'app-global-search-results',
  templateUrl: './global-search-results.component.html',
  styleUrls: ['./global-search-results.component.scss'],
  standalone: true,
  imports: [MatIconModule, MatProgressSpinnerModule, CurrencyPipe]
})
export class GlobalSearchResultsComponent implements OnInit, OnDestroy {

  moduleKey = '';
  moduleLabel = '';
  moduleIcon = 'search';
  query = '';

  isLoading = false;
  errorMessage: string | null = null;
  invalidModule = false;
  tooShort = false;
  data: GlobalSearchModulePage | null = null;

  /** Page number in the URL — kept separately so an error on page N can still offer "back to page 1". */
  requestedPage = 1;

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public globalSearchService: GlobalSearchService,
    private dialog: MatDialog,
    private remindersService: RemindersService,
    private toasterService: ToasterService
  ) {}

  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap]).pipe(
      takeUntil(this.destroy$),
      map(([params, query]) => ({
        module: params.get('module') ?? '',
        q: this.globalSearchService.normalizeQuery(query.get('q')),
        page: Math.max(1, parseInt(query.get('page') ?? '1', 10) || 1),
      })),
      tap(({ module, q, page }) => {
        const config = this.globalSearchService.getModuleConfig(module);
        this.requestedPage = page;
        this.moduleKey = module;
        this.moduleLabel = config?.label ?? '';
        this.moduleIcon = config?.icon ?? 'search';
        this.query = q;
        this.invalidModule = !config;
        this.tooShort = !!config && q.length < GLOBAL_SEARCH_MIN_LENGTH;
        this.errorMessage = null;
        this.data = null;
      }),
      switchMap(({ module, q, page }) => {
        if (this.invalidModule || this.tooShort) {
          this.isLoading = false;
          return of(null);
        }
        this.isLoading = true;
        return this.globalSearchService.searchModule(module, q, page, PER_PAGE).pipe(
          catchError(err => {
            this.errorMessage = this.describeError(err);
            return of(null);
          })
        );
      })
    ).subscribe(page => {
      this.isLoading = false;
      this.data = page;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * HTML-safe highlight for the description line only — delegates to
   * GlobalSearchService.highlightText(), the same implementation the header
   * dropdown uses, passing this page's own tracked query. Title/identifier
   * on this page stay plain text, matching their existing (pre-existing,
   * unchanged) lack of highlighting here.
   */
  highlight(text: string | null | undefined): string {
    return this.globalSearchService.highlightText(text, this.query);
  }

  /**
   * Two-letter initials for the assignee avatar badge — same algorithm as
   * AvatarGroupComponent.getInitials() (shared/avatar-group/avatar-group.component.ts)
   * and the header dropdown's identical helper, replicated here rather than
   * importing that full component for one static badge.
   */
  getInitials(name: string | null | undefined): string {
    if (!name) {
      return '';
    }
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  /**
   * The stacked-avatar badges for one result item's real team (Sales Rep/
   * Estimator/Project Manager/Assigned To/etc., per module) — delegates to
   * GlobalSearchService.getTeamAvatars(), the one shared implementation
   * also used by the header dropdown (GlobalSearchComponent).
   */
  teamAvatars(item: GlobalSearchItem): Array<{ name: string; roles: string[]; initials: string; tooltip: string; color: string; statusSvg: SafeHtml | undefined; profileImageUrl: string }> {
    return this.globalSearchService.getTeamAvatars(item);
  }

  /** The backend returns {message} for 503 and 422 (including "page exceeds the maximum navigable depth"). */
  private describeError(err: any): string {
    const message = typeof err === 'string' ? err : err?.message;
    return typeof message === 'string' && message.trim()
      ? message
      : 'Search is temporarily unavailable. Please try again.';
  }

  open(item: GlobalSearchItem): void {
    // Reminder is exempt — it never navigates at all (see below), so it
    // has no real route to require. Milestone is exempt for the same
    // reason (no synchronous fallback route exists — see
    // global-search.component.ts's identical handling).
    if (!item.route && item.module !== 'reminder' && item.module !== 'milestone') return;

    // Reminder has no dedicated page to land on — open the existing
    // AddReminderModalComponent directly, right where the user already is.
    // Mirrors global-search.component.ts's identical handling.
    if (item.module === 'reminder') {
      this.openReminderFromSearch(item.id);
      return;
    }

    // A Document attached to a Task/Ticket/Note: resolve which bigger record
    // (Job/Bid/Customer/...) it really belongs to before navigating, falling
    // back to item.route if that lookup fails or finds nothing better.
    if (item.grandparentType && item.grandparentId != null) {
      this.globalSearchService.resolveGrandparentRoute(item.grandparentType, item.grandparentId)
        .subscribe(resolved => this.router.navigateByUrl(resolved ?? item.route!));
      return;
    }

    // Task/Ticket/Note: land on the record's actual parent (Job/Bid/Customer)
    // with that parent's own Tasks/Tickets/Notes tab selected AND the
    // record's popup auto-opened on top — mirrors global-search.component.ts's
    // identical handling. Falls back to item.route for any parent type not
    // yet supported, or on error.
    if (item.module === 'task') {
      this.globalSearchService.resolveTaskParentSidebarRoute(item.id)
        .subscribe(resolved => this.router.navigateByUrl(resolved ?? item.route!));
      return;
    }
    if (item.module === 'ticket') {
      this.globalSearchService.resolveTicketParentSidebarRoute(item.id)
        .subscribe(resolved => this.router.navigateByUrl(resolved ?? item.route!));
      return;
    }
    if (item.module === 'note') {
      this.globalSearchService.resolveNoteParentSidebarRoute(item.id)
        .subscribe(resolved => this.router.navigateByUrl(resolved ?? item.route!));
      return;
    }
    // Milestone: mirrors global-search.component.ts's identical handling —
    // lands on the parent's Summary tab (no "Milestones" tab exists yet),
    // or a toast if resolution fails (no listing-page fallback exists).
    if (item.module === 'milestone') {
      this.globalSearchService.resolveMilestoneParentSidebarRoute(item.id).subscribe(resolved => {
        if (resolved) {
          this.router.navigateByUrl(resolved);
        } else {
          this.toasterService.showErrorMessage('This milestone\'s record could not be opened.');
        }
      });
      return;
    }
    this.router.navigateByUrl(item.route!);
  }

  /**
   * See global-search.component.ts's identical ensureRemindersModuleLoaded()
   * for the full reasoning (RemindersModule may not be loaded yet on
   * whichever page this "View all" results page itself was reached from).
   */
  private remindersModuleLoaded = false;
  private async ensureRemindersModuleLoaded(): Promise<void> {
    if (this.remindersModuleLoaded) {
      return;
    }
    await import('../reminders/reminders.module');
    this.remindersModuleLoaded = true;
  }

  /** See global-search.component.ts's identical openReminderFromSearch() for the full reasoning. */
  private openReminderFromSearch(id: number | string): void {
    this.remindersService.fetchReminderById(id).subscribe({
      next: async (response: any) => {
        const reminder = response?.data;
        if (!reminder) {
          this.toasterService.showErrorMessage('Reminder not found.');
          return;
        }
        await this.ensureRemindersModuleLoaded();
        this.dialog.open(AddReminderModalComponent, {
          panelClass: 'reminder-modal-panel',
          disableClose: false,
          autoFocus: true,
          data: { reminder },
        });
      },
      error: () => {
        this.toasterService.showErrorMessage(
          'This reminder could not be found or you no longer have access to it.'
        );
      },
    });
  }

  /** Go to any page. The backend caps depth at 10,000 results and answers 422 beyond it, shown as an error. */
  goToPage(page: number): void {
    if (page < 1 || (this.data && page > this.data.lastPage)) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.query, page },
      queryParamsHandling: 'merge',
    });
  }

  back(): void {
    window.history.length > 1 ? window.history.back() : this.router.navigateByUrl('/dashboard');
  }

  trackByItem(index: number, item: GlobalSearchItem): string {
    return `${item.module}:${item.id}`;
  }
}
